import React, { useContext, useEffect, useState } from "react";
import { TextInput, Dropdown, TextArea, useAlert, DownloadIcon, CancelIcon } from "@wit/mpesa-ui-components";
import { DropdownType } from "@wit/mpesa-ui-components/lib/components/dropdown/dropdown.component";
import { Formik, FormikErrors } from "formik";
import styled from "styled-components";
import { AnnouncementFormSteps, AudienceType, IAnnouncement, IIcon } from "../announcements.model";
import {
  DropdownLabelContainer,
  HomeMainContent,
  ColorContainer,
  IconDropDownContainer,
} from "../announcements.styled";
import MobileSchema from "./mobile-schema-component";
import { useTranslation } from "react-i18next";
import ColorOptions from "./color-options.component";
import IconOptions from "./icon-options.component";
import { object, string } from "yup";
import i18next from "i18next";
import HomeScreenAnnouncementBusiness from "./home-screen-announcement-business.component";
import { Row } from "../../../shared/shared.styled";
import { AnnouncementsUtils } from "../announcements.utils";
import { ThumbnailImagesFields } from "./announcements-step-homescreen-card-consumer.component";
import { AlertTypeEnum } from "@wit/mpesa-ui-components/lib/context/alert/alert.context";
import DropzoneBackground from "./dropzone-background.component";
import LoadingComponent from "../../../shared/components/loading-component/LoadingComponent";
import { formatBytes } from "../../../shared/shared.utils";
import { ConfigContext } from "../../../app.component";
import AnnouncementsApi from "../announcements.api";

interface IAnnouncementsStepHomescreenCardBusinessProps {
  announcement: IAnnouncement;
  setAnnoucement: React.Dispatch<React.SetStateAction<IAnnouncement>>;
  isEditing: boolean;
  onSubmitForm: (values?: IAnnouncement) => void;
  onReady: React.Dispatch<React.SetStateAction<boolean>>;
  hideTitle?: boolean;
  setEditingHomescreenCard?: React.Dispatch<React.SetStateAction<boolean>>;
  selectedAnnouncementSavedData?: IAnnouncement;
  setSelectedAnnouncementSavedData?: React.Dispatch<React.SetStateAction<IAnnouncement>>;
}

/**
 * function to validate the form
 */
const validateStepTwo = (isNewBusinessCard?: boolean) => {
  if (isNewBusinessCard) {
    return object().shape({});
  } else {
    return object().shape({
      title: string()
        .required(i18next.t("commons.mandatoryField"))
        .max(60, i18next.t("pages.announcements.errors.maxTitleHome")),
      smallDescription: string()
        .required(i18next.t("commons.mandatoryField"))
        .max(24, i18next.t("pages.announcements.errors.maxDescriptionHome")),
      color: string()
        .required(i18next.t("commons.mandatoryField"))
        .matches(/^#([0-9A-F]{3}){1,2}$/i, i18next.t("commons.invalidColor")),
      icon: string().required(i18next.t("commons.mandatoryField")),
    });
  }
};

/**
 * component for the second step of the announcment
 */
const AnnouncementsStepHomescreenCardBusiness = ({
  announcement,
  setAnnoucement,
  isEditing,
  onSubmitForm,
  onReady,
  hideTitle = false,
  setEditingHomescreenCard,
  selectedAnnouncementSavedData,
  setSelectedAnnouncementSavedData,
}: IAnnouncementsStepHomescreenCardBusinessProps) => {
  const [t] = useTranslation();
  const { config } = useContext(ConfigContext);
  const colorDefault = "#2fc56d";
  const [tabDropColor, setTabDropColor] = React.useState(0);
  const [open, setOpen] = useState(false);
  const iconAnnouncement: IIcon =
    AnnouncementsUtils.getIconSelected(announcement.icon).category === null
      ? AnnouncementsUtils.getIconSelected(undefined)
      : AnnouncementsUtils.getIconSelected(announcement.icon);
  const [iconSelected, setIconSelected] = React.useState<IIcon>(iconAnnouncement);

  const [isLoading, setIsLoading] = useState(false);
  const [uploadImage, setUploadImage] = useState(false);
  const [showAlert, hideAlert, setAlertProps] = useAlert();
  const [errorUpload, setErrorUpload] = useState(false);

  const [thumbnailFileInfo, setThumbnailFileInfo] = React.useState<any>();
  const [thumbnailFileInfoCarousel, setThumbnailFileInfoCarousel] = React.useState<any>();
  const isNewBusinessCard = AnnouncementsUtils.validateIsNewBusinessCard(config, announcement.targetVersions);

  /**
   * function that will be call for onSubmit form
   * @param values values of the announcemt
   */
  const onSubmitStepTwo = (values: IAnnouncement) => {
    setAnnoucement(values);
    onSubmitForm(values);
  };

  /**
   * function that will be call for onChange form
   * @param values values of announcement
   */
  const onChangeValues = (values: IAnnouncement) => {
    setAnnoucement(values);
    if (isNewBusinessCard) {
      onReady(!!values.thumbnailImage);
    } else {
      onReady(!!values.title && !!values.smallDescription && !!values.icon);
    }
  };

  useEffect(() => {
    onChangeValues(announcement);
    if (!isEditing) {
      (document.getElementById(AnnouncementFormSteps.STEP_THREE) as HTMLFormElement).reset();
    }
  }, [announcement]);

  /**
   * Render image name
   * @param values - Announcement form values
   * @param thumbnailImageField - Thumbnail image field
   * @returns {string} image name and extension
   */
  const renderImageNameAndExtension = (values: IAnnouncement, thumbnailImageField: ThumbnailImagesFields): string => {
    if (thumbnailImageField === ThumbnailImagesFields.THUMBNAIL_IMAGE_NEW) {
      return thumbnailFileInfoCarousel
        ? thumbnailFileInfoCarousel.name
        : `${t(`pages.announcements.detailPage.rows.carousel`)}.${
            values[thumbnailImageField]
              ? values[thumbnailImageField]
                  ?.split(",")[0]
                  .split("/")[1]
                  .split(";")[0]
              : "png"
          }`;
    }

    return thumbnailFileInfo
      ? thumbnailFileInfo.name
      : `${t(`pages.announcements.detailPage.rows.thumbnail`)}.${
          values[thumbnailImageField]
            ? values[thumbnailImageField]
                ?.split(",")[0]
                .split("/")[1]
                .split(";")[0]
            : "png"
        }`;
  };

  /**
   * Render image size
   * @param values - Announcement form values
   * @param thumbnailImageField - Thumbnail image field
   * @returns {string} image size
   */
  const renderImageSize = (values: IAnnouncement, thumbnailImageField: ThumbnailImagesFields): string => {
    let size = 0;
    if (thumbnailImageField === ThumbnailImagesFields.THUMBNAIL_IMAGE_NEW && thumbnailFileInfoCarousel) {
      size = thumbnailFileInfoCarousel?.size || 0;
    } else if (thumbnailImageField === ThumbnailImagesFields.THUMBNAIL_IMAGE && thumbnailFileInfo) {
      size = thumbnailFileInfo?.size || 0;
    } else if (values[thumbnailImageField]) {
      size = values[thumbnailImageField]?.length || 0;
    } else if (announcement[thumbnailImageField]) {
      size = announcement[thumbnailImageField]?.length || 0;
    }

    return formatBytes(size);
  };

  /**
   * function to clear image selected
   */
  const clearImage = (setFieldValue: any, thumbnailImageField: ThumbnailImagesFields) => {
    setFieldValue(thumbnailImageField, "");
    onChangeValues({ ...announcement, [thumbnailImageField]: "" });
    setThumbnailFileInfo("");
  };

  /**
   * Check if the image dimensions are valid
   * @function validateImageForOnLoad
   * @param {number} width Image width
   * @param {number} height Image height
   * @param {number} imageAspectRatio Image aspect ratio
   * @param {boolean} isNewImage true if the new image field exist
   * @returns {boolean}
   */
  const validateImageForOnLoad = (
    width: number,
    height: number,
    imageAspectRatio: number,
    isNewImage: boolean,
  ): boolean => {
    const constValues = AnnouncementsUtils.getImageConstraintValues(false, isNewImage, announcement.appPlacement);

    let isImageValid = false;
    if (width >= constValues.MIN_WIDTH && height >= constValues.MIN_HEIGHT) {
      if (
        imageAspectRatio !== constValues.VALID_RATIO &&
        !AnnouncementsUtils.isSafaricomTab(false, announcement.appPlacement)
      ) {
        setAlertProps({
          type: AlertTypeEnum.ERROR,
          title: t("pages.announcements.detailPage.rows.imageError"),
          content: t("pages.announcements.detailPage.rows.aspectRatioError", { ratio: constValues.RATIO_STRING }),
        });
        showAlert();
      } else {
        isImageValid = true;
      }
    } else {
      setAlertProps({
        type: AlertTypeEnum.ERROR,
        title: t("pages.announcements.detailPage.rows.imageError"),
        content: t("pages.announcements.detailPage.rows.minDimensionSizeAnnouncementCard", {
          width: constValues.MIN_WIDTH,
          height: constValues.MIN_HEIGHT,
        }),
      });
      showAlert();
    }

    return isImageValid;
  };

  /**
   *This handles the on drop (uplaod) image event
   * @param acceptedImage
   * @param {IAnnouncement} values Announcement form values
   * @param {Function} setFieldValue
   * @param {ThumbnailImagesFields} thumbnailImageField iMAGE field name
   */
  const onDrop = (
    acceptedImage: File[],
    values: IAnnouncement,
    setFieldValue: any,
    thumbnailImageField: ThumbnailImagesFields,
  ) => {
    const isNewImage = thumbnailImageField === ThumbnailImagesFields.THUMBNAIL_IMAGE_NEW;
    const constValues = AnnouncementsUtils.getImageConstraintValues(false, isNewImage, announcement.appPlacement);

    if (!acceptedImage[0]) {
      setAlertProps({
        type: AlertTypeEnum.ERROR,
        title: t("pages.announcements.detailPage.rows.imageError"),
      });
      showAlert();
    } else if (acceptedImage[0] && acceptedImage[0].size > constValues.MAX_FILE_SIZE) {
      setAlertProps({
        type: AlertTypeEnum.ERROR,
        title: t("pages.announcements.detailPage.rows.imageError"),
        content: t("pages.announcements.detailPage.rows.maxSizeErrorAnnouncementCard", {
          size: constValues.MAX_FILE_SIZE / 1000,
        }),
      });
      showAlert();
    } else {
      const file: File = acceptedImage[0];
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadstart = function() {
        setUploadImage(true);
      };
      reader.onerror = function() {
        setErrorUpload(true);
      };
      reader.onloadend = function(e) {
        const img = new Image();
        img.src = e.target !== null && e.target.result !== null ? (e.target.result as string) : "";

        img.onload = function() {
          const width = img.naturalWidth || img.width;
          const height = img.naturalHeight || img.height;
          const imageAspectRatio = width / height;

          if (validateImageForOnLoad(width, height, imageAspectRatio, isNewImage)) {
            setFieldValue(thumbnailImageField, reader.result);
            if (isNewImage) {
              setThumbnailFileInfoCarousel({
                name: file.name,
                size: file.size,
              });
            } else {
              setThumbnailFileInfo({
                name: file.name,
                size: file.size,
              });
            }
            onChangeValues({
              ...values,
              [thumbnailImageField]: reader.result as string,
            });
          }
          setUploadImage(false);
        };
      };
    }
  };

  /**
   * Render background image field
   * @returns {JSX.Element}
   */
  const renderUploadImageField = (
    values: IAnnouncement,
    errors: FormikErrors<IAnnouncement>,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void,
    title: string,
    thumbnailImageField: ThumbnailImagesFields,
  ): JSX.Element => {
    const constValues = AnnouncementsUtils.getImageConstraintValues(
      true,
      thumbnailImageField === ThumbnailImagesFields.THUMBNAIL_IMAGE_NEW,
      announcement.appPlacement,
    );

    return (
      <>
        <DropzoneTitle mandatory={isEditing}>{title}</DropzoneTitle>

        {!values[thumbnailImageField] && isEditing && !uploadImage && !isLoading ? (
          <>
            <DropzoneBackground
              inputName={thumbnailImageField}
              onDrop={(file: any) => onDrop(file, values, setFieldValue, thumbnailImageField)}
              accept={".jpg, .png,"}
              multiple={false}
              dropText={t("pages.announcements.detailPage.rows.dropImage")}
              releaseText={t("pages.announcements.detailPage.rows.uploadImage")}
              error={errors[thumbnailImageField] ? !!errors[thumbnailImageField] : false}
            />
            <ImageInfo isEditing={isEditing} hasValue={announcement && !!announcement[thumbnailImageField]}>
              <ImageInfoTitle>{t("pages.announcements.detailPage.rows.recommendations")}</ImageInfoTitle>
              <ImageInfoDescription>
                {AnnouncementsUtils.isSafaricomTab(false, values.appPlacement) ? (
                  <>
                    {t("pages.announcements.detailPage.rows.imageRequirementsAnnouncementCardSafaricomTab", {
                      width: constValues.MIN_WIDTH,
                      height: constValues.MIN_HEIGHT,
                    })}
                  </>
                ) : (
                  t("pages.announcements.detailPage.rows.imageRequirementsAnnouncementCard", {
                    width: constValues.MIN_WIDTH,
                    height: constValues.MIN_HEIGHT,
                    ratio: constValues.RATIO_STRING,
                    size: constValues.MAX_FILE_SIZE / 1000,
                  })
                )}
              </ImageInfoDescription>
            </ImageInfo>
          </>
        ) : isLoading || uploadImage ? (
          <LoadingComponent />
        ) : !isEditing && errorUpload ? (
          <ImageDiv>
            <ErrorUploadImages>
              <DownloadIcon />
            </ErrorUploadImages>
            <DescriptionErrorImage>{t("pages.announcements.detailPage.rows.imageLoadingError")}</DescriptionErrorImage>
          </ImageDiv>
        ) : announcement[thumbnailImageField] ? (
          <ImageDiv>
            <ImageWrapper
              backgroundImage={
                isEditing ? (values[thumbnailImageField] as string) : (announcement[thumbnailImageField] as string)
              }
              isEditing={isEditing}
              isCarouselImg={thumbnailImageField === ThumbnailImagesFields.THUMBNAIL_IMAGE_NEW}
            >
              <span onClick={() => clearImage(setFieldValue, thumbnailImageField)}>
                <CancelIcon />
              </span>
            </ImageWrapper>
            <DescriptionImage
              isEditing={isEditing}
              isCarouselImg={thumbnailImageField === ThumbnailImagesFields.THUMBNAIL_IMAGE_NEW}
            >
              <NameImage>{renderImageNameAndExtension(values, thumbnailImageField)}</NameImage>
              <SizeImage>{renderImageSize(values, thumbnailImageField)}</SizeImage>
            </DescriptionImage>
          </ImageDiv>
        ) : (
          <NoImageUploaded>No image selected</NoImageUploaded>
        )}
      </>
    );
  };

  /**
   * request image to backend
   * @param tab number of tab
   */
  const requestThumbnailUrl = () => {
    if (!announcement.thumbnailImage && announcement.thumbnailImageUrl) {
      setIsLoading(true);
      setErrorUpload(false);
      AnnouncementsApi.methods
        .getImage(announcement.thumbnailImageUrl)
        .then(
          res => {
            setAnnoucement({
              ...announcement,
              thumbnailImage: res.data,
              thumbnailImageCarousel: "",
            });
            if (setSelectedAnnouncementSavedData && selectedAnnouncementSavedData) {
              setSelectedAnnouncementSavedData({
                ...selectedAnnouncementSavedData,
                thumbnailImage: res.data,
              });
            }
          },
          () => {
            setErrorUpload(true);
          },
        )
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
      setErrorUpload(false);
    }
  };

  React.useEffect(() => {
    if (!isEditing) {
      requestThumbnailUrl();
    }
  }, []);

  return (
    <Formik
      initialValues={announcement.color ? announcement : { ...announcement, color: colorDefault }}
      onSubmit={onSubmitStepTwo}
      validationSchema={validateStepTwo(isNewBusinessCard)}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ values, handleChange, isSubmitting, setFieldValue, handleSubmit, resetForm, errors, setValues }) => (
        <HomeMainContent>
          <HomeScreenMain>
            <HomeScreenTitle hidden={hideTitle}>{`${
              AnnouncementsUtils.isConsumerAudience(values.audience as AudienceType)
                ? t("pages.announcements.detailPage.rows.consumer")
                : t("pages.announcements.detailPage.rows.business")
            } ${t("pages.announcements.detailPage.rows.secondTitle")}`}</HomeScreenTitle>
            <SectionForm
              id={AnnouncementFormSteps.STEP_THREE}
              onSubmit={handleSubmit}
              onChange={() => {
                onChangeValues(values);
              }}
              onReset={() => {
                if (
                  (isNewBusinessCard && announcement.thumbnailImageUrl) ||
                  (!isNewBusinessCard && announcement.icon && announcement.title && announcement.smallDescription)
                ) {
                  if (selectedAnnouncementSavedData) {
                    setValues(selectedAnnouncementSavedData);
                    setAnnoucement(selectedAnnouncementSavedData);
                  } else {
                    resetForm(announcement);
                  }
                } else if (setEditingHomescreenCard) {
                  setEditingHomescreenCard(true);
                }
              }}
            >
              {isNewBusinessCard &&
                renderUploadImageField(
                  values,
                  errors,
                  setFieldValue,
                  t("pages.announcements.detailPage.rows.backgroundImage"),
                  ThumbnailImagesFields.THUMBNAIL_IMAGE,
                )}
              <TextAreaSection removeMargin={hideTitle} isNewBusinessAnnouncements={isNewBusinessCard}>
                {!isEditing ? (
                  <>
                    <DefineTitle required={false}>{t("pages.announcements.detailPage.rows.title")}</DefineTitle>
                    <NotEditingText>{announcement.title}</NotEditingText>
                  </>
                ) : (
                  <TextArea
                    value={values.title ? values.title : ""}
                    //@ts-ignore
                    title={
                      isNewBusinessCard ? (
                        t("pages.announcements.detailPage.rows.title")
                      ) : (
                        <DefineTextArea>{t("pages.announcements.detailPage.rows.title")}</DefineTextArea>
                      )
                    }
                    placeholder={t("pages.announcements.detailPage.rows.placeholderHomeTitle")}
                    onChange={e => setFieldValue("title", e.target.value)}
                    error={errors.title ? errors.title : undefined}
                    style={{ color: "#333333" }}
                    maxLength={60}
                  />
                )}
              </TextAreaSection>
              <TextInputSection>
                {!isEditing ? (
                  <>
                    <DefineTitle required={false}>
                      {t("pages.announcements.detailPage.rows.smallDescription")}
                    </DefineTitle>
                    <NotEditingText>{announcement.smallDescription}</NotEditingText>
                  </>
                ) : (
                  <TextInput
                    value={values.smallDescription ? values.smallDescription : ""}
                    required={isNewBusinessCard ? false : true}
                    title={t("pages.announcements.detailPage.rows.smallDescription")}
                    placeholder={t("pages.announcements.detailPage.rows.placeholderHomeDescription")}
                    onChange={e => setFieldValue("smallDescription", e.target.value)}
                    error={errors.smallDescription ? errors.smallDescription : undefined}
                    maxLength={24}
                  />
                )}
              </TextInputSection>
              {!isNewBusinessCard && (
                <SectionFormRow>
                  <ColorSection>
                    <DefineTitle required={isEditing}>{t("pages.announcements.detailPage.rows.color")}</DefineTitle>

                    {!isEditing ? (
                      <Row>
                        <BigBadgeColor color={announcement.color ? announcement.color : colorDefault} />

                        <NotEditingText>{announcement.color ? announcement.color : colorDefault}</NotEditingText>
                      </Row>
                    ) : (
                      <Dropdown
                        hasValue={false}
                        label={
                          <DropdownLabelContainer>
                            <ColorContainer backgroundColor={values.color ? values.color : colorDefault} />{" "}
                            <SpanInput>{values.color ? values.color : colorDefault}</SpanInput>
                          </DropdownLabelContainer>
                        }
                        dropdownType={DropdownType.RECTANGULAR_NORMAL}
                        isOpen={open}
                        controlledToggleOpen={setOpen}
                        error={errors.color}
                      >
                        <ColorOptions
                          tab={tabDropColor}
                          setTab={setTabDropColor}
                          color={values.color !== undefined ? values.color : colorDefault}
                          setColor={setFieldValue}
                          textToSave={"color"}
                          show={open}
                          setShow={setOpen}
                        />
                      </Dropdown>
                    )}
                  </ColorSection>

                  <IconSection>
                    <DefineTitle required={isEditing}>{t("pages.announcements.detailPage.rows.icon")}</DefineTitle>

                    {!isEditing ? (
                      <Row>
                        <IconBadge logo={iconAnnouncement.base64} />

                        <NotEditingText>{iconAnnouncement.label}</NotEditingText>
                      </Row>
                    ) : (
                      <IconOptionsContainer>
                        <Dropdown
                          hasValue={false}
                          label={
                            <DropdownLabelContainer>
                              <IconDropDownContainer logo={iconSelected.base64} />

                              <SpanInput>{iconSelected.label}</SpanInput>
                            </DropdownLabelContainer>
                          }
                          dropdownType={DropdownType.RECTANGULAR_NORMAL}
                          error={errors.icon}
                        >
                          <IconOptions
                            iconID={values.icon ? values.icon : ""}
                            setIconID={(textToSave, icon) => {
                              setFieldValue(textToSave, icon);

                              onChangeValues({ ...values, icon });
                            }}
                            setIcon={setIconSelected}
                            textToSave={"icon"}
                          />
                        </Dropdown>
                      </IconOptionsContainer>
                    )}
                  </IconSection>
                </SectionFormRow>
              )}
            </SectionForm>
          </HomeScreenMain>
          <MainSideBarColumn>
            {AnnouncementsUtils.isConsumerAudience(values.audience as AudienceType) ? (
              <AudienceContainer>{t("pages.announcements.detailPage.rows.consumerApp")}</AudienceContainer>
            ) : (
              <AudienceContainer>{t("pages.announcements.detailPage.rows.businessApp")}</AudienceContainer>
            )}
            <div>
              <MobileSchema appPlacement={values.appPlacement}>
                {isNewBusinessCard ? (
                  <HomeScreenAnnouncementBusiness
                    backgroundImage={(values.thumbnailImage as string) || ""}
                    valuesAnnouncement={values}
                    icon={iconSelected}
                  />
                ) : (
                  <HomeScreenAnnouncementBusiness valuesAnnouncement={values} icon={iconSelected} />
                )}
              </MobileSchema>
            </div>
          </MainSideBarColumn>
        </HomeMainContent>
      )}
    </Formik>
  );
};

const HomeScreenMain = styled("div")`
  width: 60%;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const HomeScreenTitle = styled("div")`
  font-family: Vodafone Rg;
  font-size: 22px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: ${props => props.theme.palette.darkGrey};
`;

const MainSideBarColumn = styled("div")`
  display: flex;
  flex-direction: column;
  width: 40%;
  > div {
    width: 300px;
    margin: auto;
    > div > div {
      margin-left: 4.5%;
      margin-right: 4.5%;
    }
  }
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SectionForm = styled("form")`
  margin-right: 20%;
`;

const SectionFormRow = styled("div")`
  margin-top: 3vh;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const ColorSection = styled("div")`
  flex: 0.35;
  > button > div:last-child > div > div > div:first-child > div {
    margin-left: -70px;
  }
`;

const IconSection = styled("div")`
  flex: 0.6;
`;

const TextAreaSection = styled("div")<{ removeMargin: boolean; isNewBusinessAnnouncements?: boolean }>`
  margin-top: ${props => (props.removeMargin ? "0" : "3vh")};
  font-family: Vodafone RgBd;
  font-size: 16px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.31;
  letter-spacing: normal;
  color: ${props => props.theme.palette.midGrey};
  position: relative;
  > div > span > span:last-child {
    ${props =>
      props.isNewBusinessAnnouncements
        ? `
        min-height: 21px;
        font-family: Vodafone Rg;
        font-weight: bold;
        line-height: 21px;
        font-size: 16px;
        color: #999999;
        margin-bottom: 8px;
        margin-top: 24px;`
        : `
        color: #ff2a58;
        margin-left: 0.2vw;
        font-weight: normal;`};
  }
`;

const TextInputSection = styled("div")`
  position: relative;
  margin-top: 3vh;
  font-family: Vodafone RgBd;
  font-size: 16px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.31;
  letter-spacing: normal;
  color: ${props => props.theme.palette.midGrey};

  input {
    ::placeholder {
      color: #999999;
    }
  }
`;

const DefineTitle = styled("div")<{ required: boolean }>`
  min-height: 21px;
  font-family: Vodafone Rg;
  font-weight: bold;
  line-height: 21px;
  font-size: 16px;
  color: ${props => props.theme.palette.midGrey};
  margin-bottom: 8px;
  ${props => (props.required ? `::after {content: " *";color: #ff0000;font-weight: 400;padding-left: 2px;}` : null)};
`;

const DefineTextArea = styled("span")`
  min-height: 21px;
  font-family: Vodafone Rg;
  font-weight: bold;
  line-height: 21px;
  font-size: 16px;
  color: #999999;
  margin-bottom: 8px;
  ::after {
    content: " *";
    color: #ff0000;
    font-weight: 400;
    padding-left: 2px;
  }
`;

const SpanInput = styled("span")`
  font-family: Vodafone Rg;
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.31;
  letter-spacing: normal;
  color: ${props => props.theme.palette.midGrey};
`;

const NotEditingText = styled("div")`
  font-family: Vodafone Rg;
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.31;
  letter-spacing: normal;
  color: ${props => props.theme.palette.darkGrey};
`;

const BigBadgeColor = styled("div")<{ color: string }>`
  width: 38px;
  height: 34px;
  border-radius: 5px;
  background-color: ${props => props.color};
  margin-right: 0.5vw;
`;

const IconBadge = styled("div")<{
  logo: string;
}>`
  background-color: #fff;
  background-image: ${props => (props.logo ? `url("${props.logo}")` : "none")};
  background-repeat: no-repeat;
  background-size: 24px 24px;
  background-position: center;
  width: 38px;
  height: 34px;
`;

const AudienceContainer = styled("div")`
  height: 27px;
  background-color: #2fc56d;
  border-radius: 20px;
  padding: 4px 30px;
  cursor: pointer;
  width: fit-content !important;
  color: white;
  font-family: Vodafone Rg;
  font-size: 14px;
  font-weight: normal;
  line-height: normal;
  font-style: normal;
  letter-spacing: 0.4px;
  text-align: center;
  margin-bottom: 3vh !important;
`;

const IconOptionsContainer = styled("div")`
  width: 100%;
  > button > div {
    width: 100%;
  }
`;

const DropzoneTitle = styled("div")<{ mandatory: boolean }>`
  min-height: 21px;
  font-family: Vodafone Rg;
  font-weight: bold;
  line-height: 21px;
  font-size: 16px;
  color: #999999;
  margin-bottom: 8px;
  margin-top: 24px;
  width: fit-content;
  display: inline-flex;
  ${(props: { mandatory: any }) =>
    props.mandatory ? "::after {content: ' *'; color: #ff0000; font-weight: 400; padding-left: 2px;}" : null};
`;

const ImageInfo = styled("div")<{ isEditing: boolean; hasValue: boolean }>`
  display: ${(props: { isEditing: any; hasValue: any }) => (!props.isEditing || props.hasValue ? "none" : "flex")};
  flex-direction: column;
`;

const ImageInfoTitle = styled("span")`
  font-family: Vodafone Rg;
  font-size: 12px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  color: ${(props: { theme: { palette: { midGrey: any } } }) => props.theme.palette.midGrey};
`;

const ImageInfoDescription = styled("span")`
  font-family: Vodafone Rg;
  font-size: 12px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  color: ${(props: { theme: { palette: { midGrey: any } } }) => props.theme.palette.midGrey};
`;

const ImageDiv = styled("div")`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const ErrorUploadImages = styled("div")`
  width: 50px;
  height: 50px;
  display: flex;
  flex-direction: column;
  flex: 0.08;
  align-items: center;
  justify-content: center;
  svg {
    width: 32px;
    height: 32px;
    stroke: ${props => props.theme.palette.errorRed};
  }
`;

const DescriptionErrorImage = styled("div")`
  display: flex;
  flex-direction: column;
  flex: 0.9;
  justify-content: center;
  font-family: Vodafone Rg;
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.31;
  letter-spacing: normal;
  color: ${props => props.theme.palette.errorRed};
`;

const ImageWrapper = styled("div")<{
  backgroundImage: string;
  isEditing: boolean;
  isCarouselImg: boolean;
}>`
    position: relative;
    width: ${(props: { isEditing: any }) => (props.isEditing ? "56" : "90")}px;
    height: ${(props: { isEditing: any }) => (props.isEditing ? "56" : "90")}px;
    max-width: ${(props: { isEditing: any; isCarouselImg: boolean }) => {
      if (props.isCarouselImg) {
        return props.isEditing ? "112" : "180";
      }
      return "";
    }}px;
    background-image: url("${props => props.backgroundImage}");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 3px;
    flex: ${(props: { isEditing: any; isCarouselImg: boolean }) => {
      if (props.isCarouselImg) {
        return "0.26";
      }
      return props.isEditing ? "0.08" : "0.125";
    }};
    >span{
      position: absolute;
      background: #aaa;
      z-index: 0;
      width: 15px;
      height: 15px;
      border-radius: 50%;
      right: -0.5vw;
      top: -0.5vh;
      display:${(props: { isEditing: any }) => (props.isEditing ? "flex" : "none")};
      justify-content: center;
      align-items: center;
      cursor: pointer;
      svg{
        stroke: #fff;
        width: 15px;
        height: 15px;
      }
    }
`;

const DescriptionImage = styled("div")<{
  isEditing: boolean;
  isCarouselImg: boolean;
}>`
  flex: ${(props: { isEditing: any; isCarouselImg: boolean }) => {
    if (props.isCarouselImg) {
      return props.isEditing ? "0.84" : "0.72";
    }
    return props.isEditing ? "0.88" : "0.835";
  }};
  align-self: center;
  ${props => (props.isCarouselImg ? "padding-left: 0.5rem" : null)}
`;

const NameImage = styled("div")`
  font-family: Vodafone Rg;
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #000000;
  margin-bottom: 4px;
`;

const NoImageUploaded = styled("div")`
  font-family: Vodafone Rg;
  font-size: 0.85rem;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #000000;
  padding: 0 1rem;
  margin-bottom: 1rem;
`;

const SizeImage = styled("div")`
  font-family: Vodafone Rg;
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.36;
  letter-spacing: normal;
  color: #afafaf;
`;

export default AnnouncementsStepHomescreenCardBusiness;
